/**
 * Accessible dialog — the `.modal-backdrop` / `.modal` block of the vanilla
 * pages, plus the accessibility the old markup lacked.
 *
 * Behaviour:
 *   - Escape closes, a click on the BACKDROP closes, a click inside does not.
 *   - `role="dialog" aria-modal="true"`, labelled by the title element.
 *   - Focus moves into the panel on open and returns to the previously focused
 *     element on close; Tab/Shift+Tab are trapped inside the panel. A form
 *     field leading the body gets the focus, otherwise the panel itself — a
 *     link or button would show a focus ring nobody asked for (DEV-103).
 *   - Background scrolling is locked while the dialog is open.
 */

import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

const FIELD = 'textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled])';
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  /** German dialog title. */
  title: ReactNode;
  /** Optional muted line below the title. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Buttons rendered in the sticky footer. Omit for a content-only dialog. */
  footer?: ReactNode;
  /** Called by Escape, the backdrop click and the close button. */
  onClose: () => void;
  /** Panel width preset. */
  size?: 'narrow' | 'default' | 'wide';
  /** Set false while a mutation is in flight to prevent accidental dismissal. */
  dismissable?: boolean;
}

export function Modal({
  title,
  subtitle,
  children,
  footer,
  onClose,
  size = 'default',
  dismissable = true,
}: ModalProps): ReactNode {
  const { t } = useLingui();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const subtitleId = useId();
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const requestClose = useCallback(() => {
    if (dismissable) onClose();
  }, [dismissable, onClose]);

  // Move focus into the panel on mount, restore it on unmount.
  useEffect(() => {
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(`.body :is(${FOCUSABLE})`);
    (first?.matches(FIELD) ? first : panel)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, []);

  // Lock background scrolling.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Escape to close + focus trap.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [requestClose]);

  const sizeClass = size === 'default' ? '' : ` ${size}`;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        // Only a press that starts AND ends on the backdrop closes the dialog.
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        className={`modal${sizeClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        ref={panelRef}
      >
        <header>
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId}>{title}</h2>
            {subtitle ? (
              <div className="sub" id={subtitleId}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="close-x"
            onClick={requestClose}
            disabled={!dismissable}
            aria-label={t`Dialog schließen`}
          >
            <Icon name="close" />
          </button>
        </header>
        <div className="body">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </div>
  );
}

export default Modal;
